import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

function normalizeSkuSearchTerm(term) {
  const value = term.trim();
  const isSkuLike =
    value.length >= 2 &&
    value.length <= 32 &&
    /^[a-z0-9/_-]+$/i.test(value) &&
    (/\d/.test(value) || value.length <= 4 || /[\/_-]/.test(value));

  return isSkuLike && !value.endsWith('*') ? `${value}*` : value;
}

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  connectedCallback() {
    super.connectedCallback();

    const { searchPageInput } = this.refs;
    searchPageInput.form?.addEventListener('submit', this.#handleSubmit);

    if (searchPageInput.value.endsWith('*')) {
      searchPageInput.value = searchPageInput.value.slice(0, -1);
    }
  }

  disconnectedCallback() {
    this.refs.searchPageInput.form?.removeEventListener('submit', this.#handleSubmit);
    super.disconnectedCallback();
  }

  #handleSubmit = () => {
    this.refs.searchPageInput.value = normalizeSkuSearchTerm(this.refs.searchPageInput.value);
  };

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
